import { classNames } from "@greeter/util";
import { useEffect, useRef, useState } from "react";
import { TimeOfDay, toDateOnlyString } from "@greeter/date";

import { set } from "date-fns";
import { Log } from "@greeter/log";

import css from "./DateTimeInput.module.scss";

function canParse(s: string): boolean {
  const d = new Date(s);
  return !isNaN(Number(d));
}

export type DateTimeInputProps = {
  rawValue?: string;
  onRawChange?: (s: string) => void;

  value?: Date;
  onValueChange?: (c?: Date) => void;

  label?: React.ReactNode;
  error?: React.ReactNode;
  warning?: React.ReactNode;
} & React.ComponentProps<"div">;

export function DateTimeInput({ className, ...props }: DateTimeInputProps) {
  const dateFromInputRef = useRef<HTMLInputElement | null>(null);
  const timeFromInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const value = props.value ?? new Date();

    if (!dateFromInputRef.current) return;
    dateFromInputRef.current.value = toDateOnlyString(value);
    if (!timeFromInputRef.current) return;
    timeFromInputRef.current.value = TimeOfDay.fromDate(value).toString();
  }, [props.value]);

  return (
    <div className={css.DateTimeInputContainer}>
      <div
        {...classNames(
          css.DateTimeInput,
          props.error
            ? "error-outline"
            : props.warning
            ? "warning-outline"
            : undefined
        )}
      >
        <div className={css.DateInputContainer}>
          {typeof props.label === "string" ? (
            <label className={css.DateTimeTitle}>{props.label}</label>
          ) : (
            props.label
          )}
          <input
            ref={dateFromInputRef}
            {...classNames(css.Input, css.DateInput)}
            type="date"
            onBlur={(ev) => {
              const parseable = canParse(ev.currentTarget.value);
              if (!parseable) {
                Log.debug("[DateTimeInput] could not parse the Date value", {
                  input: ev.currentTarget.value,
                });
                return;
              }
              const d = new Date(ev.currentTarget.value);
              Log.debug("[DateTimeInput] parsed as date", { d });
              props.onValueChange?.(
                set(props.value ? props.value : new Date(), {
                  year: d.getFullYear(),
                  month: d.getMonth(),
                  date: d.getDate(),
                })
              );
              props.onBlur?.(ev);
            }}
          />
        </div>
        <input
          ref={timeFromInputRef}
          {...classNames(css.Input, css.TimeInput)}
          type="time"
          onBlur={(ev) => {
            try {
              const timeOfDay = TimeOfDay.parse(ev.currentTarget.value);
              props.onValueChange?.(
                set(props.value ? props.value : new Date(), {
                  hours: timeOfDay.hours,
                  minutes: timeOfDay.minutes,
                  seconds: timeOfDay.seconds,
                  milliseconds: 0,
                })
              );
            } catch (error) {
              Log.debug(
                "[DateTimeInput] could not parse value as a TimeOfDay",
                { input: ev.currentTarget.value }
              );
            }
            props.onBlur?.(ev);
          }}
        />
      </div>
      {props.error}
      {!props.error && props.warning}
    </div>
  );
}
